import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import {
  ArrowLeft,
  Check,
  X,
  Map as MapIcon,
  ExternalLink,
  Eye,
  EyeOff,
  Keyboard,
  Megaphone,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import FlagBadge from '../../components/moderation/FlagBadge';
import VisibilityBadge from '../../components/moderation/VisibilityBadge';
import RejectModal from '../../components/moderation/RejectModal';
import MapModal from '../../components/moderation/MapModal';
import PhotoMap from '../../components/moderation/PhotoMap';
import {
  formatBytes,
  formatLisbon,
  isPhotoPublic,
  maskIp,
  photoSignature,
} from '../../utils/moderationFormat';

const ShortcutsModal = ({ onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
      <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Atalhos</h2>
      <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
        <li><kbd className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">A</kbd> Aprovar</li>
        <li><kbd className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">R</kbd> Rejeitar</li>
        <li><kbd className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">M</kbd> Mapa</li>
        <li><kbd className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">J</kbd> / <kbd className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">K</kbd> Próxima / anterior</li>
        <li><kbd className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">?</kbd> Esta lista</li>
      </ul>
    </div>
  </div>
);

const PhotoDetailPage = () => {
  const { photoId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [showMap, setShowMap] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showFullIp, setShowFullIp] = useState(false);
  const [showExif, setShowExif] = useState(false);
  const [shouldPublish, setShouldPublish] = useState(false);
  const [shouldTweet, setShouldTweet] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);

  const { data: photo, isLoading, error } = useQuery(
    ['moderation-photo', photoId],
    () => apiService.getModerationPhoto(photoId),
    { retry: false },
  );

  const { data: queue } = useQuery(
    ['moderation-queue', 1],
    () => apiService.listModerationPhotos({ status: 'pending', page: 1, per_page: 100 }),
    { staleTime: 60_000 },
  );

  const { data: settings } = useQuery('moderation-settings', () => apiService.getModerationSettings());

  const queueIds = (queue?.data || []).map((p) => p.id);
  const currentIdx = queueIds.indexOf(photoId);
  const nextId = currentIdx >= 0 && currentIdx < queueIds.length - 1 ? queueIds[currentIdx + 1] : null;
  const prevId = currentIdx > 0 ? queueIds[currentIdx - 1] : null;

  useEffect(() => {
    setShouldPublish(false);
    setShouldTweet(false);
    setShowPublishConfirm(false);
  }, [photoId]);

  const goNext = useCallback(() => {
    if (nextId) navigate(`/moderation/photos/${nextId}`);
    else navigate('/moderation/queue');
  }, [nextId, navigate]);

  const approveMut = useMutation(
    ({ publish, twitter }) =>
      apiService.approveModerationPhoto(photoId, {
        fire_id: photo?.fire_id,
        flags_snapshot: photo?.flags,
        photo_snapshot: photo,
        publish: !!publish,
        twitter: !!twitter,
      }),
    {
      onSuccess: (_data, vars) => {
        toast.success(
          vars.publish
            ? vars.twitter
              ? 'Foto aprovada e publicada (incl. Twitter)'
              : 'Foto aprovada e publicada (sem Twitter)'
            : 'Foto aprovada',
        );
        setShowPublishConfirm(false);
        setShouldPublish(false);
        setShouldTweet(false);
        goNext();
      },
      onError: (err) => {
        const data = err?.response?.data;
        if (err?.response?.status === 409 && data?.error === 'already_moderated') {
          toast(data.message || 'Foto já moderada por outro moderador', { icon: 'ℹ️' });
          setShowPublishConfirm(false);
          goNext();
          return;
        }
        toast.error(data?.message || 'Falha ao aprovar');
      },
    },
  );

  const photoIsPublic = isPhotoPublic(photo);
  const canPublish = photoIsPublic;
  const effectivePublish = shouldPublish && canPublish;

  const triggerApprove = useCallback(() => {
    if (effectivePublish) setShowPublishConfirm(true);
    else approveMut.mutate({ publish: false, twitter: false });
  }, [approveMut, effectivePublish]);

  const rejectMut = useMutation(
    ({ reason, note }) =>
      apiService.rejectModerationPhoto(photoId, {
        reason,
        note,
        fire_id: photo?.fire_id,
        flags_snapshot: photo?.flags,
        photo_snapshot: photo,
      }),
    {
      onSuccess: () => {
        toast.success('Foto rejeitada');
        setShowReject(false);
        goNext();
      },
      onError: (err) => {
        const data = err?.response?.data;
        if (err?.response?.status === 409 && data?.error === 'already_moderated') {
          toast(data.message || 'Foto já moderada por outro moderador', { icon: 'ℹ️' });
          setShowReject(false);
          goNext();
          return;
        }
        toast.error(data?.message || 'Falha ao rejeitar');
      },
    },
  );

  useEffect(() => {
    const handler = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
      if (showReject || showMap || showShortcuts || showPublishConfirm) return;
      const k = e.key.toLowerCase();
      if (k === 'a') {
        e.preventDefault();
        triggerApprove();
      } else if (k === 'r') {
        e.preventDefault();
        setShowReject(true);
      } else if (k === 'm') {
        e.preventDefault();
        setShowMap(true);
      } else if (k === 'j') {
        e.preventDefault();
        if (nextId) navigate(`/moderation/photos/${nextId}`);
      } else if (k === 'k') {
        e.preventDefault();
        if (prevId) navigate(`/moderation/photos/${prevId}`);
      } else if (e.key === '?') {
        e.preventDefault();
        setShowShortcuts(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [triggerApprove, nextId, prevId, navigate, showReject, showMap, showShortcuts, showPublishConfirm]);

  const reasons = useMemo(
    () => settings?.rejection_reasons?.length ? settings.rejection_reasons : [
      'Irrelevante', 'Conteúdo ofensivo', 'Duplicada', 'Qualidade insuficiente',
      'Fora do contexto do incidente', 'Localização incorrecta', 'Suspeita de fake', 'Outro',
    ],
    [settings],
  );

  if (isLoading) return <LoadingSpinner />;
  if (error || !photo) {
    return (
      <div className="p-6">
        <Link to="/moderation/queue" className="text-fire-600 hover:underline">← Voltar</Link>
        <p className="mt-4 text-gray-600 dark:text-gray-400">Foto não encontrada (já moderada?).</p>
      </div>
    );
  }

  const ip = photo.client?.ip;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <Link to="/moderation/queue" className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Voltar à fila
        </Link>
        <button onClick={() => setShowShortcuts(true)} className="text-sm text-gray-500 inline-flex items-center gap-1">
          <Keyboard className="w-4 h-4" /> Atalhos
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="bg-black rounded-md overflow-hidden">
            <img src={`${photo.url}${photo.url.includes('?') ? '&' : '?'}cacheBuster=${Date.now()}-${Math.random().toString(36).slice(2)}`} alt="" className="w-full max-h-[70vh] object-contain bg-black" />
          </div>
          {photo.gps?.lat != null && photo.gps?.lng != null && (
            <div className="mt-3 rounded-md overflow-hidden border border-gray-200 dark:border-gray-700" style={{ height: '320px' }}>
              <PhotoMap
                photo={photo}
                incident={photo.incident}
                distanceKm={photo.metrics?.distance_km}
              />
            </div>
          )}
          {photo.gps?.heading_deg != null && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Cone laranja: campo de visão estimado (~60°) a partir do azimute {Math.round(photo.gps.heading_deg)}°.
              Pino azul: incidente. Pino laranja: foto.
            </p>
          )}
          <div className="flex gap-2 mt-3">
            <a
              href={photo.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ExternalLink className="w-4 h-4" /> Abrir em nova tab
            </a>
            <button
              onClick={() => setShowMap(true)}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <MapIcon className="w-4 h-4" /> Ver mapa
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Visibilidade pretendida pelo utilizador
              </h3>
              <VisibilityBadge photo={photo} />
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {isPhotoPublic(photo) ? (
                <>
                  Após aprovação, fica visível em <code className="text-xs">GET /v2/incidents/&#123;id&#125;/photos</code>
                  {' '}e portanto em fogos.pt e na app.
                </>
              ) : (
                <>
                  Após aprovação, fica retida apenas para uso ANEPC.{' '}
                  <strong>Não aparece em nenhum endpoint público</strong>, mesmo que aprovada.
                </>
              )}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Submetida por{' '}
              <strong className="text-gray-900 dark:text-white">
                {photoSignature(photo) || 'utilizador anónimo'}
              </strong>
              .
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Flags</h3>
            {(photo.flags || []).length === 0 ? (
              <p className="text-sm text-primary-600 dark:text-primary-300">Nenhuma flag — candidata a fast-track.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {photo.flags.map((f, i) => <FlagBadge key={i} flag={f} />)}
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4 text-sm">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Metadata</h3>
            <dl className="grid grid-cols-3 gap-y-2 text-gray-700 dark:text-gray-300">
              <dt className="font-medium">Incidente</dt>
              <dd className="col-span-2">
                {photo.fire_id ? (
                  <a
                    href={`https://fogos.pt/fogo/${photo.fire_id}/detalhe`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-fire-600 hover:underline inline-flex items-center gap-1"
                  >
                    {photo.fire_id} <ExternalLink className="w-3 h-3" />
                  </a>
                ) : '—'}
              </dd>

              <dt className="font-medium">Local</dt>
              <dd className="col-span-2">{photo.incident?.location || photo.incident?.name || '—'}</dd>

              <dt className="font-medium">Início incêndio</dt>
              <dd className="col-span-2">{formatLisbon(photo.incident?.start_time)}</dd>

              <dt className="font-medium">GPS</dt>
              <dd className="col-span-2 font-mono text-xs">
                {photo.gps?.lat?.toFixed(5)}, {photo.gps?.lng?.toFixed(5)}
                {photo.gps?.altitude_m != null && ` · alt ${Math.round(photo.gps.altitude_m)}m`}
                {photo.gps?.heading_deg != null && ` · hdg ${Math.round(photo.gps.heading_deg)}°`}
              </dd>

              <dt className="font-medium">Tirada</dt>
              <dd className="col-span-2">{formatLisbon(photo.taken_at)}</dd>

              <dt className="font-medium">Ficheiro</dt>
              <dd className="col-span-2">
                {formatBytes(photo.size_bytes)} · {photo.width}×{photo.height} · {photo.mime}
              </dd>

              <dt className="font-medium">Assinatura</dt>
              <dd className="col-span-2">{photoSignature(photo) || <span className="text-gray-500">—</span>}</dd>

              <dt className="font-medium">IP</dt>
              <dd className="col-span-2 font-mono text-xs flex items-center gap-2">
                {showFullIp && isAdmin?.() ? (ip || '—') : maskIp(ip)}
                {isAdmin?.() && ip && (
                  <button
                    onClick={() => setShowFullIp((v) => !v)}
                    className="text-gray-500 hover:text-gray-700"
                    title={showFullIp ? 'Mascarar' : 'Mostrar completo'}
                  >
                    {showFullIp ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                )}
              </dd>

              <dt className="font-medium">User agent</dt>
              <dd className="col-span-2 text-xs break-all">{photo.client?.user_agent || '—'}</dd>

              <dt className="font-medium">App version</dt>
              <dd className="col-span-2">{photo.client?.app_version || '—'}</dd>
            </dl>
          </div>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md">
            <button
              onClick={() => setShowExif((v) => !v)}
              className="w-full text-left px-4 py-3 font-semibold text-gray-900 dark:text-white"
            >
              {showExif ? '▼' : '▶'} EXIF completo
            </button>
            {showExif && (
              <pre className="px-4 pb-4 text-xs overflow-x-auto text-gray-700 dark:text-gray-300">
                {JSON.stringify(photo.exif_raw || {}, null, 2)}
              </pre>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4 space-y-3">
            <label className={`flex items-start gap-3 ${canPublish ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
              <input
                type="checkbox"
                checked={effectivePublish}
                disabled={!canPublish}
                onChange={(e) => {
                  setShouldPublish(e.target.checked);
                  if (!e.target.checked) setShouldTweet(false);
                }}
                className="mt-1"
              />
              <span className="text-sm">
                <span className="inline-flex items-center gap-1 font-medium text-gray-900 dark:text-white">
                  <Megaphone className="w-4 h-4" />
                  Publicar nas redes sociais e notificar subscritores
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {canPublish ? (
                    <>Facebook, Telegram, Discord + push para subscritores do incidente. <strong>Acção irreversível.</strong></>
                  ) : (
                    <>O utilizador não autorizou exibição pública — a foto permanece ANEPC-only e não pode ser publicada.</>
                  )}
                </span>
              </span>
            </label>

            <label className={`flex items-start gap-3 pl-7 ${effectivePublish ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
              <input
                type="checkbox"
                checked={shouldTweet && effectivePublish}
                disabled={!effectivePublish}
                onChange={(e) => setShouldTweet(e.target.checked)}
                className="mt-1"
              />
              <span className="text-sm">
                <span className="font-medium text-gray-900 dark:text-white">
                  Também publicar no Twitter
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Sem isto, o Twitter é ignorado.
                </span>
              </span>
            </label>
          </div>

          <div className="flex gap-3">
            <button
              onClick={triggerApprove}
              disabled={approveMut.isLoading}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 text-white rounded-md disabled:opacity-50 ${
                effectivePublish
                  ? 'bg-fire-600 hover:bg-fire-700'
                  : 'bg-primary-500 hover:bg-primary-600'
              }`}
            >
              {effectivePublish ? <Megaphone className="w-5 h-5" /> : <Check className="w-5 h-5" />}
              {approveMut.isLoading
                ? 'A aprovar…'
                : effectivePublish
                ? 'Aprovar + publicar (A)'
                : 'Aprovar (A)'}
            </button>
            <button
              onClick={() => setShowReject(true)}
              disabled={rejectMut.isLoading}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-fire-500 text-white rounded-md hover:bg-fire-600 disabled:opacity-50"
            >
              <X className="w-5 h-5" /> Rejeitar (R)
            </button>
          </div>
        </div>
      </div>

      {showMap && (
        <MapModal
          photo={photo}
          incident={photo.incident}
          distanceKm={photo.metrics?.distance_km}
          onClose={() => setShowMap(false)}
        />
      )}
      {showReject && (
        <RejectModal
          reasons={reasons}
          onConfirm={(payload) => rejectMut.mutate(payload)}
          onClose={() => setShowReject(false)}
          isSubmitting={rejectMut.isLoading}
        />
      )}
      {showShortcuts && <ShortcutsModal onClose={() => setShowShortcuts(false)} />}
      {showPublishConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !approveMut.isLoading && setShowPublishConfirm(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-6 h-6 text-fire-500 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Publicar nas redes sociais?
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                  A foto será aprovada e publicada em <strong>Facebook, Telegram, Discord</strong> +
                  push para subscritores
                  {shouldTweet ? <> e também no <strong>Twitter</strong></> : <> (Twitter <strong>não</strong> será publicado)</>}.
                </p>
                <p className="text-sm text-fire-600 dark:text-fire-400 mt-2 font-medium">
                  Esta acção é irreversível — não há "unpublish" via API.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button
                onClick={() => setShowPublishConfirm(false)}
                disabled={approveMut.isLoading}
                className="px-4 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => approveMut.mutate({ publish: true, twitter: shouldTweet })}
                disabled={approveMut.isLoading}
                className="px-4 py-2 text-sm bg-fire-600 text-white rounded-md hover:bg-fire-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Megaphone className="w-4 h-4" />
                {approveMut.isLoading
                  ? 'A publicar…'
                  : shouldTweet
                  ? 'Aprovar e publicar (incl. Twitter)'
                  : 'Aprovar e publicar (sem Twitter)'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoDetailPage;
